import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, Button, Form, Alert, Container, Spinner, Row, Col } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';

const StrikeDataManager = () => {
    const [indices, setIndices] = useState({});
    const [selectedIndex, setSelectedIndex] = useState('NIFTY');
    const [ceFile, setCeFile] = useState(null);
    const [peFile, setPeFile] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const expectedHeaders = [
        'token',
        'symbol',
        'name',
        'expiry',
        'strike',
        'lotsize',
        'instrumenttype',
        'exch_seg',
        'tick_size',
    ];

    const excelDateToJSDate = (serial) => {
        const utcDays = Math.floor(serial - 25569);
        const utcValue = utcDays * 86400;
        return new Date(utcValue * 1000);
    };

    useEffect(() => {
        fetchIndices();
    }, []);

    const fetchIndices = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/indices`);
            const transformedIndices = response.data.reduce(
                (acc, { name, strikeData }) => ({
                    ...acc,
                    [name]: { strikeData: { ce: strikeData?.ce || [], pe: strikeData?.pe || [] } },
                }),
                {}
            );
            setIndices(transformedIndices);
            setError('');
        } catch (error) {
            setError(
                error.response?.status === 401
                    ? 'Session expired. Please log in again.'
                    : `Error fetching indices: ${error.response?.data?.message || error.message}`
            );
        }
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (type === 'ce') setCeFile(file);
        else setPeFile(file);
    };

    const updateStrikeData = async (type) => {
        const file = type === 'ce' ? ceFile : peFile;
        if (!file) {
            setError(`Please select a ${type.toUpperCase()} Excel file.`);
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const jsonData = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (evt) => {
                    try {
                        const data = evt.target.result;
                        const workbook = XLSX.read(data, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                        if (rawJson.length === 0) {
                            reject(new Error(`The ${type.toUpperCase()} sheet is empty.`));
                            return;
                        }

                        const fileHeaders = Object.keys(rawJson[0]).map((header) => header.toLowerCase().trim());
                        const headersValid =
                            fileHeaders.length === expectedHeaders.length &&
                            expectedHeaders.every((expHeader, index) => expHeader === fileHeaders[index]);

                        if (!headersValid) {
                            reject(new Error(`Invalid header structure in ${type.toUpperCase()} file.`));
                            return;
                        }

                        const processedJson = rawJson.map((row) => {
                            let expiryDate =
                                typeof row.expiry === 'number'
                                    ? excelDateToJSDate(row.expiry)
                                    : new Date(row.expiry);
                            const formattedExpiry = isNaN(expiryDate.getTime())
                                ? row.expiry
                                : format(expiryDate, 'dd-MMM-yy').toUpperCase();

                            return {
                                token: Number(row.token),
                                symbol: row.symbol,
                                name: row.name,
                                expiry: formattedExpiry,
                                strike: Number(row.strike),
                                lotsize: Number(row.lotsize),
                                instrumenttype: row.instrumenttype,
                                exch_seg: row.exch_seg,
                                tick_size: Number(row.tick_size),
                            };
                        });

                        const filteredJson = processedJson.filter((row) =>
                            type === 'ce' ? row.symbol.includes('CE') : row.symbol.includes('PE')
                        );

                        if (filteredJson.length === 0) {
                            reject(new Error(`No ${type.toUpperCase()} options found in ${type.toUpperCase()} file.`));
                            return;
                        }

                        resolve(filteredJson);
                    } catch (err) {
                        reject(new Error(`Error processing ${type.toUpperCase()} file: ${err.message}`));
                    }
                };
                reader.onerror = () => reject(new Error(`Error reading ${type.toUpperCase()} file.`));
                reader.readAsArrayBuffer(file);
            });

            const response = await axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${selectedIndex}`, {
                strikeData: { [type]: jsonData },
            });

            setIndices((prev) => ({
                ...prev,
                [selectedIndex]: {
                    strikeData: {
                        ce: type === 'ce' ? jsonData : prev[selectedIndex]?.strikeData?.ce || [],
                        pe: type === 'pe' ? jsonData : prev[selectedIndex]?.strikeData?.pe || [],
                    },
                },
            }));
            setSuccess(`Updated ${selectedIndex} ${type.toUpperCase()} strike data successfully`);
            setError('');
            type === 'ce' ? setCeFile(null) : setPeFile(null);
        } catch (error) {
            setError(
                error.response?.status === 401
                    ? 'Session expired. Please log in again.'
                    : error.message || `Error updating ${type.toUpperCase()} strike data: ${error.response?.data?.message || error.message}`
            );
            setSuccess('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="shadow rounded my-4 p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="border-0 p-4">
                <h3 className="mb-3">Manage Strike Data</h3>
                <Form.Group className="mb-3 w-25">
                    <Form.Label>Select Index:</Form.Label>
                    <Form.Select value={selectedIndex} onChange={(e) => setSelectedIndex(e.target.value)}>
                        <option value="">Select an Index</option>
                        {Object.keys(indices).map((idx) => (
                            <option key={idx} value={idx}>{idx}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {selectedIndex && (
                <div className="mb-3">
                    <h5>Current Strike Data for {selectedIndex}</h5>

                    <Row>
                        <Col md={6}>
                            <h6>CE Strike</h6>
                            <pre style={{ background: "#eef7ff", padding: "10px", borderRadius: "4px" }}>
                            {JSON.stringify(indices[selectedIndex]?.strikeData?.ce?.[0], null, 2)}
                            </pre>
                                                    <Form.Group className="mb-3">
                            <Form.Label>Update CE Strike Data (Excel):</Form.Label>
                            <Form.Control
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={(e) => handleFileChange(e, 'ce')}
                                className='w-50'
                            />
                            <Button
                                className="mt-2"
                                onClick={() => updateStrikeData('ce')}
                                disabled={!ceFile || loading}
                            >
                                {loading && !peFile ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Updating CE...
                                    </>
                                ) : (
                                    'Update CE'
                                )}
                            </Button>
                        </Form.Group>
                        </Col>
                        <Col md={6}>
                            <h6>PE Strike</h6>
                            <pre style={{ background: "#eef7ff", padding: "10px", borderRadius: "4px" }}>
                            {JSON.stringify(indices[selectedIndex]?.strikeData?.pe?.[0], null, 2)}
                            </pre>
                                                    <Form.Group className="mb-3">
                            <Form.Label>Update PE Strike Data (Excel):</Form.Label>
                            <Form.Control
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={(e) => handleFileChange(e, 'pe')}
                                className='w-50'
                            />
                            <Button
                                className="mt-2"
                                onClick={() => updateStrikeData('pe')}
                                disabled={!peFile || loading}
                            >
                                {loading && !ceFile ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Updating PE...
                                    </>
                                ) : (
                                    'Update PE'
                                )}
                            </Button>
                        </Form.Group>  
                        </Col>
                    </Row>
                </div>
                )}
            </Card>
        </Container>
    );
};

export default StrikeDataManager;